/**
 * Canonical Add/Edit user form section catalog (pure — no React/DOM).
 */

/**
 * @typedef {object} AdminUserFormSectionDef
 * @property {string} id
 * @property {string} label
 * @property {number} addOrder
 * @property {number} editOrder
 * @property {boolean} [studentOnly]
 * @property {boolean} [staffOnly]
 * @property {boolean} [addOnly]
 * @property {boolean} [editOnly]
 * @property {boolean} [requiresExpand]
 */

/** @type {AdminUserFormSectionDef[]} */
export const ADMIN_USER_FORM_SECTIONS = [
  { id: 'auf-basic', label: 'Basic info', addOrder: 10, editOrder: 10 },
  {
    id: 'auf-batch-course',
    label: 'Batch',
    studentOnly: true,
    addOrder: 20,
    editOrder: 20,
  },
  {
    id: 'auf-submit-primary',
    label: 'Add user',
    addOnly: true,
    addOrder: 30,
    editOrder: 999,
  },
  {
    id: 'auf-enrollment-status',
    label: 'Enrollment status',
    studentOnly: true,
    requiresExpand: true,
    addOrder: 40,
    editOrder: 30,
  },
  {
    id: 'auf-insurance',
    label: 'Insurance',
    studentOnly: true,
    requiresExpand: true,
    addOrder: 70,
    editOrder: 40,
  },
  {
    id: 'auf-counselling',
    label: 'Counselling',
    studentOnly: true,
    requiresExpand: true,
    addOrder: 80,
    editOrder: 50,
  },
  {
    id: 'auf-education',
    label: 'Education',
    studentOnly: true,
    requiresExpand: true,
    addOrder: 90,
    editOrder: 60,
  },
  {
    id: 'auf-joining-kit',
    label: 'Joining kit',
    studentOnly: true,
    requiresExpand: true,
    addOrder: 50,
    editOrder: 70,
  },
  {
    id: 'auf-parents',
    label: 'Parents',
    studentOnly: true,
    requiresExpand: true,
    addOrder: 60,
    editOrder: 80,
  },
  {
    id: 'auf-placement',
    label: 'Placement',
    studentOnly: true,
    requiresExpand: true,
    addOrder: 100,
    editOrder: 90,
  },
  {
    id: 'auf-staff-personal',
    label: 'Personal',
    staffOnly: true,
    requiresExpand: true,
    addOrder: 40,
    editOrder: 30,
  },
  {
    id: 'auf-staff-guardian',
    label: 'Guardian',
    staffOnly: true,
    requiresExpand: true,
    addOrder: 50,
    editOrder: 40,
  },
  {
    id: 'auf-staff-professional',
    label: 'Professional',
    staffOnly: true,
    requiresExpand: true,
    addOrder: 60,
    editOrder: 50,
  },
  {
    id: 'auf-staff-additional',
    label: 'Additional',
    staffOnly: true,
    requiresExpand: true,
    addOrder: 70,
    editOrder: 60,
  },
  {
    id: 'auf-submit',
    label: 'Update',
    editOnly: true,
    addOrder: 999,
    editOrder: 100,
  },
  {
    id: 'auf-change-history',
    label: 'Change history',
    studentOnly: true,
    editOnly: true,
    addOrder: 999,
    editOrder: 110,
  },
];

/**
 * @param {{ isStudent: boolean, mode: 'add' | 'edit' }} opts
 * @returns {AdminUserFormSectionDef[]}
 */
export function filterAdminUserFormSections({ isStudent, mode }) {
  const orderKey = mode === 'add' ? 'addOrder' : 'editOrder';
  return ADMIN_USER_FORM_SECTIONS.filter((section) => {
    if (section.studentOnly && !isStudent) return false;
    if (section.staffOnly && isStudent) return false;
    if (section.addOnly && mode !== 'add') return false;
    if (section.editOnly && mode !== 'edit') return false;
    return true;
  }).sort((a, b) => (a[orderKey] ?? 999) - (b[orderKey] ?? 999));
}

/**
 * @param {AdminUserFormSectionDef[]} sections
 * @param {string} currentId
 * @param {1 | -1} direction
 * @returns {AdminUserFormSectionDef|null}
 */
export function adjacentAdminUserFormSection(sections, currentId, direction) {
  if (!sections.length) return null;
  const idx = sections.findIndex((s) => s.id === currentId);
  const from = idx >= 0 ? idx : 0;
  const next = from + direction;
  if (next < 0 || next >= sections.length) return null;
  return sections[next];
}

/**
 * Smooth-scroll helper (DOM side-effect; kept tiny for reuse).
 * @param {string} sectionId
 */
export function scrollToAdminUserFormSection(sectionId) {
  const el = document.getElementById(sectionId);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.classList.remove('auf-section-pulse');
  void el.offsetWidth;
  el.classList.add('auf-section-pulse');
  return true;
}
